import { ReactNode } from 'react'
import { Alert } from '@stratascratch/ui'
import { ExecutionResults } from '@stratascratch/code-editor'
import TestOutput from './TestOutput'
import './Results.css'

interface ExtendedResults extends ExecutionResults {
  user_results?: {
    results?: {
      image?: string
      [key: string]: unknown
    }
    [key: string]: unknown
  }
  is_correct?: boolean
  failed_tests?: {
    id: string
    input: string
    user_output: string
    expected_output: string
  }[]
  author_results?: string
  is_solution_submission?: boolean
  captured_output?: string
  error?: string
}

interface Props {
  results: ExtendedResults | null
  isLoading: boolean
  questionClass: string
}

function BaseResults({
  results,
  isLoading,
  children,
}: Props & { children: ReactNode }) {
  const executionError = results?.error
  const showSubtitle = !isLoading && !results

  return (
    <div>
      <div className="mb-4 px-4">
        <h4 className="text-base font-bold">Output</h4>
        {showSubtitle && (
          <p className="dark:text-neutral-100">
            Run code to display results here
          </p>
        )}
      </div>

      {executionError ? (
        <Alert type="danger" noIcon>
          {executionError}
        </Alert>
      ) : (
        <>
          {children}
          {/* Render the image for Visual Questions */}
          {results?.user_results?.results?.image && (
            <div>
              <img
                src={`data:image/png;base64,${results.user_results.results.image}`}
                alt="Execution Result"
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

function SolutionResults({ results, isLoading, questionClass }: Props) {
  const executionOutput = results?.captured_output
  const isCorrect = results?.is_correct
  const failedTests = results?.failed_tests

  return (
    <BaseResults
      questionClass={questionClass}
      results={results}
      isLoading={isLoading}
    >
      {executionOutput && <pre className="mt-4 px-4">{executionOutput}</pre>}

      {questionClass === 'Algorithm Question' && (
        <>
          {isCorrect === false && (
            <div className="m-2">
              <Alert
                type="danger"
                noIcon
                className="QuestionResults__incorrect-message"
              >
                Solution is incorrect. For a solution to be accepted, your
                output for every test should match the output of the official
                solution.
              </Alert>
            </div>
          )}
          {isCorrect === true && (
            <div className="m-2">
              <Alert
                type="success"
                noIcon
                className="QuestionResults__correct-message"
              >
                Solution is correct! All tests passed.
              </Alert>
            </div>
          )}

          {failedTests && (
            <>
              <h3 className="mt-6 px-4 text-base font-bold">
                Failed Test Cases
              </h3>
              {failedTests.map((test) => (
                <div className="m-2" key={test.id}>
                  <TestOutput>
                    <div className="mb-6 mt-6 px-4">
                      <span className="text-base font-bold">
                        Test {test.id}
                      </span>
                      <div className="mt-2 pb-6">
                        Input:
                        <span className="mt-4 px-4">{test.input}</span>
                      </div>
                      <span>
                        <h4 className="font-bold">Your output:</h4>
                        <span className="mt-4 px-4">{test.user_output}</span>
                      </span>
                      <br />
                      <span>
                        <h4 className="font-bold">Official output: </h4>
                        <span className="mt-4 px-4">
                          {test.expected_output}
                        </span>
                      </span>
                    </div>
                  </TestOutput>
                </div>
              ))}
            </>
          )}
        </>
      )}
    </BaseResults>
  )
}

function TestResults({ results, isLoading, questionClass }: Props) {
  const executionOutput = results?.captured_output
  const tableResults = results?.user_results?.results
  const authorTableResults = results?.author_results
  const isCorrect = results?.is_correct

  return (
    <BaseResults
      questionClass={questionClass}
      results={results}
      isLoading={isLoading}
    >
      {!executionOutput && tableResults === null && (
        <Alert type="info">No result</Alert>
      )}
      {executionOutput && <pre className="mt-4 px-4">{executionOutput}</pre>}

      {questionClass === 'Algorithm Question' && (
        <>
          {isCorrect === true && (
            <div className="m-2">
              <Alert
                type="success"
                className="QuestionResults__correct-message"
              >
                Your output is correct!
              </Alert>
            </div>
          )}
          {isCorrect === false && (
            <div className="m-2">
              <Alert
                type="danger"
                className="QuestionResults__incorrect-message"
              >
                Your output is incorrect.
              </Alert>
              <TestOutput>
                <h4 className="mt-6 px-4 text-base font-bold">
                  Official output:
                </h4>
                <pre className="mb-4 mt-4 px-4">{authorTableResults}</pre>
              </TestOutput>
            </div>
          )}
          {tableResults && (
            <div className="m-2">
              <TestOutput>
                <h4 className="mt-6 px-4 text-base font-bold">Your output:</h4>
                <pre className="mb-4 mt-4 px-4">
                  {JSON.stringify(tableResults)}
                </pre>
              </TestOutput>
            </div>
          )}
        </>
      )}
    </BaseResults>
  )
}

function Results({ results, isLoading, questionClass }: Props) {
  return results?.is_solution_submission ? (
    <SolutionResults
      results={results}
      isLoading={isLoading}
      questionClass={questionClass}
    />
  ) : (
    <TestResults
      results={results}
      isLoading={isLoading}
      questionClass={questionClass}
    />
  )
}

export default Results
